import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';
import { Button } from '../common/Common';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, KeyRound } from 'lucide-react';
import './ForcePasswordModal.css';

// Accounts created at signup log in with a temporary password. This modal is
// deliberately non-dismissible — no close button, no backdrop click, no Esc —
// so the temporary password can't be left in place.

const strengthScore = (pw) => {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 6) s++;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^\w\s]/.test(pw)) s++;
  return Math.min(s, 4);
};
const strengthLabel = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'];
const strengthColor = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#10b981'];

const ForcePasswordModal = () => {
  const { logout, user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const score = strengthScore(next);
  const match = next && confirm && next === confirm;
  const mismatch = confirm && !match;
  const canSubmit = current && next.length >= 6 && match && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setFeedback(null);
    setSaving(true);
    try {
      const res = await apiFetch('auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      if (res.success) {
        setFeedback({ type: 'success', msg: 'Password updated. Signing you out — log back in with your new password.' });
        setCurrent(''); setNext(''); setConfirm('');
        setTimeout(() => { logout(); }, 1800);
      } else {
        setFeedback({ type: 'error', msg: res.message || 'Could not change password' });
      }
    } catch {
      setFeedback({ type: 'error', msg: 'Connection failed' });
    }
    setSaving(false);
  };

  return (
    <div className="fpm__overlay" role="dialog" aria-modal="true" aria-labelledby="fpm-title">
      <div className="fpm">
        <div className="fpm__head">
          <div className="fpm__icon"><KeyRound size={20} /></div>
          <div>
            <h3 className="fpm__title" id="fpm-title">Password change required</h3>
            <p className="fpm__sub">
              Signed in as <strong>{user?.username || 'you'}</strong>
            </p>
          </div>
        </div>

        <p className="fpm__lead">
          You signed in with a temporary password. Set your own password to continue —
          enter the temporary one as your current password.
        </p>

        <form onSubmit={handleSubmit} className="fpm__form">
          <div className="fpm__field">
            <label>Temporary Password</label>
            <div className="fpm__input">
              <Lock size={15} />
              <input
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                placeholder="Password from your email"
                autoComplete="current-password"
                autoFocus
                required
              />
              <button type="button" className="fpm__toggle" onClick={() => setShowCurrent(v => !v)} tabIndex={-1}>
                {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div className="fpm__field">
            <label>New Password</label>
            <div className="fpm__input">
              <Lock size={15} />
              <input
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                minLength={6}
                required
              />
              <button type="button" className="fpm__toggle" onClick={() => setShowNext(v => !v)} tabIndex={-1}>
                {showNext ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {next && (
              <div className="fpm__strength">
                <div className="fpm__bars">
                  {[0, 1, 2, 3].map(i => (
                    <span
                      key={i}
                      className="fpm__bar"
                      style={{ background: i < score ? strengthColor[score] : 'var(--gray-200)' }}
                    />
                  ))}
                </div>
                <span style={{ color: strengthColor[score] }}>{strengthLabel[score]}</span>
              </div>
            )}
          </div>

          <div className="fpm__field">
            <label>Confirm New Password</label>
            <div className={`fpm__input ${mismatch ? 'fpm__input--error' : ''}`}>
              <Lock size={15} />
              <input
                type={showNext ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter new password"
                autoComplete="new-password"
                required
              />
            </div>
            {mismatch && <span className="fpm__err">Passwords do not match</span>}
          </div>

          {feedback && (
            <div className={`fpm__alert fpm__alert--${feedback.type}`}>
              {feedback.type === 'success' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
              <span>{feedback.msg}</span>
            </div>
          )}

          <div className="fpm__actions">
            <button type="button" className="fpm__logout" onClick={logout}>
              Sign out instead
            </button>
            <Button variant="gold" type="submit" disabled={!canSubmit}>
              {saving ? 'Updating…' : 'Update Password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordModal;
